import { doc, getDoc, setDoc, updateDoc, serverTimestamp, onSnapshot } from 'firebase/firestore'
import { db } from '../firebase'

// Dev-only logger — keeps [topPicksSync] info chatter out of the production
// console. Genuine errors still use console.error directly.
const devLog = (...args) => { if (import.meta.env.DEV) console.log(...args) }

// Admin allowlist — emails that get isAdmin: true on first sign-in.
export const ADMIN_EMAILS = new Set([
  'thepitlogapp@gmail.com',
  'kevint427@gmail.com',
])
const isAdminEmail = (email) => !!email && ADMIN_EMAILS.has(email.toLowerCase())

/** Returns true if the URL is a Google-hosted photo (not a custom avatar). */
function isGooglePhotoUrl(url) {
  return typeof url === 'string' && url.includes('googleusercontent.com')
}

/** Read username from the action_user localStorage blob (the real storage key). */
function getLocalUsername() {
  try {
    const raw = localStorage.getItem('action_user')
    return raw ? JSON.parse(raw)?.username || null : null
  } catch {
    return null
  }
}

/** Read custom avatar from localStorage (stored separately from the user blob). */
function getLocalAvatar() {
  try {
    return localStorage.getItem('action_avatar') || null
  } catch {
    return null
  }
}

/** Read the locally-cached permanently-unlocked themes (used to migrate
 *  offline-earned unlocks into a fresh user doc on first sign-in). */
function getLocalPermUnlockedThemes() {
  try {
    const raw = localStorage.getItem('action_perm_themes')
    const parsed = raw ? JSON.parse(raw) : null
    if (!Array.isArray(parsed)) return ['extra']
    return parsed.includes('extra') ? parsed : ['extra', ...parsed]
  } catch {
    return ['extra']
  }
}

export async function loadOrCreateUserDoc(user) {
  const ref = doc(db, 'users', user.uid)
  const snap = await getDoc(ref)

  if (snap.exists()) {
    // Always trust what Firestore has — never overwrite with the Google display name.
    await updateDoc(ref, { lastSeen: serverTimestamp() })
    const data = snap.data()

    // Resolve the avatar to return:
    // 1. Custom avatar from Firestore (not a Google URL) — highest priority
    // 2. Custom avatar from localStorage
    // 3. Whatever is in Firestore (Google URL or null) — fallback
    const firestoreAvatar = data.avatar && !isGooglePhotoUrl(data.avatar) ? data.avatar : null
    const localAvatar = getLocalAvatar()
    const resolvedAvatar = firestoreAvatar || localAvatar || data.avatar || null

    return { isNew: false, data: { ...data, avatar: resolvedAvatar } }
  }

  // Brand new user — migrate from localStorage if available, then fall back.
  // Capture the full streak (current + longest + lastPlayedDate) so Firestore
  // is the complete source of truth — lastPlayedDate is essential for the
  // streak to keep incrementing across sign-outs / device switches.
  const streakRaw = localStorage.getItem('action_streak')
  let streakNum = 0
  let streakLongest = 0
  let streakLastPlayed = null
  try {
    const parsed = JSON.parse(streakRaw)
    streakNum = parsed?.currentStreak ?? parseInt(streakRaw) ?? 0
    streakLongest = parsed?.longestStreak ?? streakNum
    streakLastPlayed = parsed?.lastPlayedDate ?? null
  } catch {
    streakNum = parseInt(streakRaw) || 0
    streakLongest = streakNum
  }

  // For a new user, prefer any custom avatar already in localStorage over their Google photo.
  const localAvatar = getLocalAvatar()
  const isAdmin = isAdminEmail(user.email)
  const newDoc = {
    uid: user.uid,
    email: user.email || null,
    // Priority: localStorage username → Google display name → fallback
    username: getLocalUsername() || user.displayName || 'MovieFan',
    // Priority: custom localStorage avatar → Google photo
    avatar: localAvatar || user.photoURL || null,
    tickets: isAdmin ? 999 : 10,
    streak: streakNum,
    streakLongest,
    streakLastPlayed,
    // Migrate any locally-cached permanently-unlocked themes so an offline
    // unlock survives the first sign-in. The 'extra' baseline is guaranteed.
    permUnlockedThemes: getLocalPermUnlockedThemes(),
    completedDailies: {},
    isAdmin,
    createdAt: serverTimestamp(),
    lastSeen: serverTimestamp(),
  }
  await setDoc(ref, newDoc)
  return { isNew: true, data: newDoc }
}

export async function syncUsernameToFirestore(uid, username) {
  await updateDoc(doc(db, 'users', uid), { username, lastSeen: serverTimestamp() })
}

export async function syncAvatarToFirestore(uid, avatar) {
  await updateDoc(doc(db, 'users', uid), { avatar, lastSeen: serverTimestamp() })
}

export async function syncTicketsToFirestore(uid, tickets) {
  await updateDoc(doc(db, 'users', uid), { tickets, lastSeen: serverTimestamp() })
}

/**
 * Real-time listener for a user's ticket count.
 * Calls onTickets(count) whenever tickets change in Firestore (including
 * from other devices). Returns an unsubscribe function.
 */
export function subscribeToUserTickets(uid, onTickets) {
  const ref = doc(db, 'users', uid)
  return onSnapshot(
    ref,
    (snap) => {
      if (snap.exists()) {
        const val = snap.data().tickets
        if (val != null) onTickets(val)
      }
    },
    () => {}, // silently ignore listener errors (offline, permissions)
  )
}

export async function syncStreakToFirestore(uid, streak) {
  // streak is the full object {currentStreak, longestStreak, lastPlayedDate}.
  // Persist ALL THREE — Firestore is the source of truth. lastPlayedDate is
  // critical: without it, after a sign-out / device switch the next daily
  // completion can't tell yesterday was played and resets the streak to 1.
  await updateDoc(doc(db, 'users', uid), {
    streak: streak.currentStreak ?? 0,
    streakLongest: streak.longestStreak ?? streak.currentStreak ?? 0,
    streakLastPlayed: streak.lastPlayedDate ?? null,
    lastSeen: serverTimestamp(),
  })
}

export async function syncThemeToFirestore(uid, themeId) {
  await updateDoc(doc(db, 'users', uid), { theme: themeId })
}

/**
 * Persist the user's permanently-unlocked themes list to Firestore.
 * Used by the 7-day streak unlock path — perm-only mutation, no ticket
 * change. The 'extra' baseline is enforced here too in case a caller
 * forgets.
 */
export async function syncPermUnlockedThemesToFirestore(uid, ids) {
  const arr = Array.isArray(ids) ? ids : []
  const withBaseline = arr.includes('extra') ? arr : ['extra', ...arr]
  await updateDoc(doc(db, 'users', uid), {
    permUnlockedThemes: withBaseline,
    lastSeen: serverTimestamp(),
  })
}

/**
 * Atomic ticket-purchase-unlock write. Updates BOTH the ticket count and the
 * permanently-unlocked themes list in a single Firestore updateDoc so a
 * partial failure can't leave the doc inconsistent (tickets debited but
 * theme not added — or vice versa).
 */
export async function syncThemePurchaseToFirestore(uid, tickets, ids) {
  const arr = Array.isArray(ids) ? ids : []
  const withBaseline = arr.includes('extra') ? arr : ['extra', ...arr]
  await updateDoc(doc(db, 'users', uid), {
    tickets,
    permUnlockedThemes: withBaseline,
    lastSeen: serverTimestamp(),
  })
}

/**
 * Persists the user's Top 3 movies and actors to their users/{uid} doc.
 * Uses merge: true so it never clobbers other fields. Errors are logged
 * (prefix [topPicksSync]) but never thrown — a sync failure must not crash
 * or block the UI; the local copy is always written separately.
 */
export async function syncTopPicksToFirestore(uid, { topMovies, topActors }) {
  devLog(`[topPicksSync] writing to Firestore for uid: ${uid}`)
  try {
    await setDoc(
      doc(db, 'users', uid),
      {
        topMovies: topMovies ?? [],
        topActors: topActors ?? [],
        lastSeen: serverTimestamp(),
      },
      { merge: true },
    )
  } catch (err) {
    console.error('[topPicksSync] Firestore write failed:', err)
  }
}

/**
 * Reduce each chain item to only the fields needed for display, dropping all
 * other TMDB metadata (overview, genre_ids, vote_count, popularity, character,
 * credit_id, original_title, original_language, release_date, adult, video,
 * order, softcore, …) so the Firestore completion doc stays small.
 *   Actors → { type, id, name, profile_path }
 *   Movies → { type, id, title, poster_path }
 */
function slimChain(chain) {
  if (!Array.isArray(chain)) return chain ?? null
  return chain.map((item) => {
    if (!item || typeof item !== 'object') return item
    if (item.type === 'movie') {
      return { type: item.type, id: item.id ?? null, title: item.title ?? null, poster_path: item.poster_path ?? null }
    }
    return { type: item.type, id: item.id ?? null, name: item.name ?? null, profile_path: item.profile_path ?? null }
  })
}

/**
 * Write a single daily completion to Firestore under users/{uid}.completedDailies.{date}.
 * Note: serverTimestamp() can't be used inside a map value, so completedAt is a JS Date.
 */
export async function syncDailyCompletionToFirestore(uid, date, data) {
  await updateDoc(doc(db, 'users', uid), {
    [`completedDailies.${date}`]: {
      puzzleNumber: data.puzzleNumber ?? null,
      moves: data.moves ?? null,
      timeSeconds: data.timeSeconds ?? null,
      time: data.time ?? null,
      chain: slimChain(data.chain),
      emojiChain: data.emojiChain ?? null,
      hintsUsed: data.hintsUsed ?? null,
      completedAt: new Date(),
    },
    lastSeen: serverTimestamp(),
  })
}

/**
 * Bulk-upload any localStorage daily completions Firestore doesn't have yet.
 * Used during login to cover offline / pre-sync completions.
 */
export async function syncMissingDailiesToFirestore(uid, localCompletions, firestoreCompletions) {
  const updates = {}
  for (const [date, data] of Object.entries(localCompletions || {})) {
    if ((firestoreCompletions || {})[date]) continue
    // Preserve original completedAt if available — fall back to "now"
    let completedAt = new Date()
    if (data.completedAt) {
      const parsed = new Date(data.completedAt)
      if (!Number.isNaN(parsed.getTime())) completedAt = parsed
    }
    updates[`completedDailies.${date}`] = {
      puzzleNumber: data.puzzleNumber ?? null,
      moves: data.moves ?? null,
      timeSeconds: data.timeSeconds ?? null,
      time: data.time ?? null,
      chain: slimChain(data.chain),
      emojiChain: data.emojiChain ?? null,
      hintsUsed: data.hintsUsed ?? null,
      completedAt,
    }
  }
  if (Object.keys(updates).length === 0) return
  updates.lastSeen = serverTimestamp()
  await updateDoc(doc(db, 'users', uid), updates)
}
